import { AxiosError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { MainService } from '~/shared/api/services/main'
import { handleAxiosError, parseJSON } from '~/shared/lib'
import { STORES_STORAGE_KEY } from './config'
import { FiltersBySelect, Loaders, Pages } from '../api/services/global/types'
import { GlobalService } from '../api/services/global'
import { Stores } from '../api/services/stores/types'
import { Manufacturer } from '../api'

export class GlobalModel {
  loading = false

  private storesForbiddenContext: {
    sessionToken: string | null
    legalEntityId?: string
  } | null = null

  loaders: Loaders = {
    filters: false,
    categories: false,
  }

  stores: Stores[] = parseJSON(localStorage.getItem(STORES_STORAGE_KEY)) || []

  filtersBySelect: FiltersBySelect = {
    state: [
      { id: 'created', name: 'created' },
      { id: 'assembly', name: 'assembly' },
      { id: 'dispatch', name: 'dispatch' },
      { id: 'shipped', name: 'shipped' },
      { id: 'payment invoice', name: 'payment invoice' },
      { id: 'cancel', name: 'cancel' },
      { id: 'completed', name: 'completed' },
      { id: 'countries', name: 'countries' },
    ],
    store: parseJSON(localStorage.getItem(STORES_STORAGE_KEY)) || [],
  }

  manufacturers: Manufacturer[] = []

  manufacturersLoading = false

  requestSettings = {
    limit: 20,
    offset: 20,
  }

  requestSettingsCategories = {
    limit: 20,
    offset: 20,
  }

  filtersQ = { orderPage: '', productPage: '', marketingPage: '' }

  isEndCategoryList = false

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFiltersQ = (pageFilter: keyof Pages, value: string) => {
    this.filtersQ = { ...this.filtersQ, [pageFilter]: value }
  }

  resetStoresForbiddenContext = () => {
    this.storesForbiddenContext = null
  }

  clearStores = () => {
    this.storesForbiddenContext = null
    this.stores = []
    this.filtersBySelect = { ...this.filtersBySelect, store: [] }
    localStorage.setItem(STORES_STORAGE_KEY, JSON.stringify([]))
  }

  getStores = async (legalEntityId?: string) => {
    const sessionToken = localStorage.getItem('sessionToken')
    const forbiddenContext = this.storesForbiddenContext

    if (
      forbiddenContext?.sessionToken === sessionToken &&
      (!legalEntityId || forbiddenContext.legalEntityId === legalEntityId)
    ) {
      return []
    }

    if (forbiddenContext) {
      this.storesForbiddenContext = null
    }

    try {
      this.loading = true
      const { data } = await MainService.getStores({ silentForbidden: true })

      runInAction(() => {
        this.stores = data
        this.filtersBySelect = { ...this.filtersBySelect, store: data }
      })
      localStorage.setItem(STORES_STORAGE_KEY, JSON.stringify(data))

      return data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 403) {
        this.storesForbiddenContext = { sessionToken, legalEntityId }
        runInAction(() => {
          this.stores = []
          this.filtersBySelect = { ...this.filtersBySelect, store: [] }
        })
        localStorage.setItem(STORES_STORAGE_KEY, JSON.stringify([]))

        return []
      }

      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  getFilters = async (
    names: Array<'products' | 'payments' | 'shipping' | 'countries'>,
    q?: string,
    loadMore?: boolean,
  ) => {
    this.activeLoader('filters')
    if (!loadMore) {
      this.requestSettings.offset = 0
      this.requestSettings.limit = 20
    }
    try {
      const filterPromises = names.map(async (name) => {
        const params: Record<string, number | string> = {}

        if (q?.trim()) {
          params.q = q
        }

        if (name === 'products') {
          params.limit = this.requestSettings.limit
          params.offset = this.requestSettings.offset
        }

        const { data } = await GlobalService.getFilters(name, { params })
        const key = (
          name === 'payments' ? 'payment' : name
        ) as keyof FiltersBySelect

        runInAction(() => {
          if (name === 'products') {
            this.filtersBySelect = {
              ...this.filtersBySelect,
              [key]: loadMore
                ? [...(this.filtersBySelect[key] ?? []), ...data]
                : data,
            }
            this.requestSettings.offset += this.requestSettings.limit
          } else {
            this.filtersBySelect = {
              ...this.filtersBySelect,
              [key]: data,
            }
          }
        })
      })
      await Promise.all(filterPromises)
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('filters')
      })
    }
  }

  getCategories = async (action: 'reset' | 'loadMore', page: keyof Pages) => {
    if (this.isEndCategoryList && action === 'loadMore') return

    if (['reset'].includes(action)) {
      this.requestSettingsCategories.offset = 0
      this.requestSettingsCategories.limit = 20
    }

    runInAction(() => {
      this.activeLoader('categories')
    })

    try {
      const params: Record<string, number | string> = {
        limit: this.requestSettingsCategories.limit,
        offset: this.requestSettingsCategories.offset,
      }
      if (this.filtersQ[page]?.trim()) {
        params.q = this.filtersQ[page]
      }

      const { data } = await GlobalService.getFilters('categories', { params })
      runInAction(() => {
        this.isEndCategoryList =
          data.length < this.requestSettingsCategories.limit
        this.requestSettingsCategories.offset +=
          this.requestSettingsCategories.limit
        if (action === 'loadMore' && this.filtersBySelect.categories) {
          this.filtersBySelect.categories = [
            ...this.filtersBySelect.categories,
            ...data,
          ]
        } else {
          this.filtersBySelect.categories = data
        }
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.config.data)
      }
    } finally {
      runInAction(() => {
        this.closeLoader('categories')
      })
    }
  }

  getManufacturers = async (q?: string) => {
    try {
      this.manufacturersLoading = true

      const params: Record<string, string> = {}

      if (q?.trim()) {
        params.q = q.trim()
      }

      const { data } = await GlobalService.getManufacturers({ params })
      runInAction(() => {
        this.manufacturers = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.manufacturersLoading = false
      })
    }
  }
}
export const globalStore = new GlobalModel()
